import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';

function TeamChecklists({ user }) {
  const [teamMembers, setTeamMembers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [formData, setFormData] = useState({
    templateId: '',
    propertyId: '',
    targetCompletionDate: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersData, templatesData, assignmentsData] = await Promise.all([
        api.get('/users'),
        api.get('/templates'),
        api.get('/assignments'),
      ]);

      // Filter to direct reports
      const directReports = usersData.filter(u => u.reportsTo === user.id);
      setTeamMembers(directReports);

      // Filter active templates
      const activeTemplates = templatesData.filter(t => t.isActive);
      setTemplates(activeTemplates);

      // Get assignments created by current user
      const myAssignments = assignmentsData.filter(a => a.assignedByUserId === user.id);
      setAssignments(myAssignments);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAssignModal = (member) => {
    setSelectedMember(member);
    setFormData({
      templateId: '',
      propertyId: member.propertyAssignments && member.propertyAssignments.length > 0 ? member.propertyAssignments[0] : '',
      targetCompletionDate: '',
    });
    setError('');
    setShowAssignModal(true);
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setSelectedMember(null);
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      await api.post('/assignments', {
        templateId: formData.templateId,
        assignedToUserId: selectedMember.id,
        propertyId: formData.propertyId || null,
        targetCompletionDate: formData.targetCompletionDate ? new Date(formData.targetCompletionDate).toISOString() : null,
      });

      await loadData();
      handleCloseAssignModal();
    } catch (err) {
      setError(err.message);
    }
  };

  const getMemberAssignments = (memberId) => {
    return assignments.filter(a => a.assignedToUserId === memberId);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'COMPLETED': return 'success';
      case 'IN_PROGRESS': return 'warning';
      case 'OVERDUE': return 'danger';
      default: return 'neutral';
    }
  };

  const getStatusLabel = (status) => {
    return status.replace('_', ' ');
  };

  const getTemplateName = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    return template ? template.name : 'Unknown Template';
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Team Checklists</h1>
        <p className="page-subtitle">Assign and monitor checklists for your direct reports</p>
      </div>

      {/* Self-Assign Card */}
      <div className="card" style={{ marginBottom: '1.5rem', backgroundColor: 'var(--primary-blue-100)', border: '2px solid var(--primary-blue-600)' }}>
        <div className="card-header" style={{ backgroundColor: 'transparent', borderBottom: '1px solid var(--primary-blue-600)' }}>
          <div>
            <h2 className="card-title">Test Checklist Yourself</h2>
            <p style={{ fontSize: '0.875rem', color: 'var(--gray-700)', marginTop: '0.25rem' }}>
              Assign a checklist to yourself for testing
            </p>
          </div>
          <button
            className="btn btn-primary"
            onClick={() => handleOpenAssignModal(user)}
            data-testid="self-assign-btn"
          >
            📝 Self-Assign Checklist
          </button>
        </div>
      </div>

      {teamMembers.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">👥</div>
            <h3 className="empty-state-title">No Direct Reports</h3>
            <p className="empty-state-description">
              You don't have any team members reporting to you at the moment.
              <br />
              Use the Self-Assign feature above to test checklists yourself!
            </p>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1.5rem' }}>
          {teamMembers.map((member) => {
            const memberAssignments = getMemberAssignments(member.id);
            const activeCount = memberAssignments.filter(a => a.status !== 'COMPLETED').length;
            const completedCount = memberAssignments.filter(a => a.status === 'COMPLETED').length;

            return (
              <div key={member.id} className="card">
                <div className="card-header">
                  <div>
                    <h2 className="card-title">{member.name}</h2>
                    <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)', marginTop: '0.25rem' }}>
                      {member.email} • {member.role.replace('_', ' ')}
                    </p>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={() => handleOpenAssignModal(member)}
                    data-testid={`assign-to-${member.id}`}
                  >
                    + Assign Checklist
                  </button>
                </div>

                <div style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'flex', gap: '2rem', marginBottom: '1.5rem' }}>
                    <div>
                      <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--primary-blue-600)' }}>
                        {activeCount}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Active</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '2rem', fontWeight: '700', color: 'var(--success-600)' }}>
                        {completedCount}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Completed</div>
                    </div>
                  </div>

                  {memberAssignments.length === 0 ? (
                    <div style={{ 
                      padding: '2rem',
                      textAlign: 'center',
                      backgroundColor: 'var(--gray-100)',
                      borderRadius: '6px'
                    }}>
                      <p style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                        No checklists assigned yet
                      </p>
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: '0.75rem' }}>
                      {memberAssignments.map((assignment) => (
                        <div
                          key={assignment.id}
                          style={{
                            padding: '1rem',
                            backgroundColor: 'var(--gray-50)',
                            border: '1px solid var(--gray-300)',
                            borderRadius: '6px',
                          }}
                          data-testid={`assignment-${assignment.id}`}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ flex: 1 }}>
                              <h3 style={{ fontSize: '0.9375rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                                {getTemplateName(assignment.templateId)}
                              </h3>
                              <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--gray-600)' }}>
                                <span>Started: {new Date(assignment.startDate).toLocaleDateString()}</span>
                                {assignment.targetCompletionDate && (
                                  <span>Due: {new Date(assignment.targetCompletionDate).toLocaleDateString()}</span>
                                )}
                              </div>
                            </div>
                            <span className={`badge badge-${getStatusColor(assignment.status)}`}>
                              {getStatusLabel(assignment.status)}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && selectedMember && (
        <div className="modal-overlay" onClick={handleCloseAssignModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Assign Checklist to {selectedMember.name}</h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {error && <div className="form-error" style={{ marginBottom: '1rem' }}>{error}</div>}

                <div className="form-group">
                  <label className="form-label" htmlFor="templateId">Checklist Template *</label>
                  <select
                    id="templateId"
                    className="form-select"
                    value={formData.templateId}
                    onChange={(e) => setFormData({ ...formData, templateId: e.target.value })}
                    required
                    data-testid="assign-template-select"
                  >
                    <option value="">Select a template...</option>
                    {templates.map((template) => (
                      <option key={template.id} value={template.id}>
                        {template.name} ({template.type})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="propertyId">Property (Optional)</label>
                  <select
                    id="propertyId"
                    className="form-select"
                    value={formData.propertyId}
                    onChange={(e) => setFormData({ ...formData, propertyId: e.target.value })}
                    data-testid="assign-property-select"
                  >
                    <option value="">No specific property</option>
                    {selectedMember.propertyAssignments && selectedMember.propertyAssignments.map((propId) => (
                      <option key={propId} value={propId}>
                        Property {propId}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="targetCompletionDate">Target Completion Date (Optional)</label>
                  <input
                    id="targetCompletionDate"
                    type="date"
                    className="form-input"
                    value={formData.targetCompletionDate}
                    onChange={(e) => setFormData({ ...formData, targetCompletionDate: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="assign-target-date"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleCloseAssignModal}
                  data-testid="cancel-assign"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  data-testid="confirm-assign"
                >
                  Assign Checklist
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TeamChecklists;
